/**
 * Identity-hygiene — TanStack Query hooks.
 *
 * The dormant-access list is the primary read; the rebuild
 * mutation is the operator-side button to backfill from the
 * audit log.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { withStepUp } from "@/lib/with-step-up";
import {
  campaignListSchema,
  campaignSchema,
  campaignItemListSchema,
  dormantListSchema,
  policyProposalSchema,
  policySchema,
  revokeLogListSchema,
  type Campaign,
  type CampaignItem,
  type DormantFilterSearch,
  type DormantList,
  type ItemDecision,
  type Policy,
  type PolicyForm,
  type PolicyProposal,
  type RevokeLogList,
} from "./schemas";

function base(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/identity/hygiene`;
}

const PAGE_SIZE = 50;

function buildQueryString(filter: DormantFilterSearch): string {
  const params = new URLSearchParams();
  if (filter.tone) params.set("tone", filter.tone);
  if (filter.grant_source) params.set("grant_source", filter.grant_source);
  if (filter.application_id) params.set("application_id", filter.application_id);
  if (filter.user_id) params.set("user_id", filter.user_id);
  if (filter.min_days_idle != null) {
    params.set("min_days_idle", String(filter.min_days_idle));
  }
  if (filter.include_fresh) params.set("include_fresh", "true");
  const page = filter.page ?? 1;
  params.set("limit", String(PAGE_SIZE));
  params.set("offset", String((page - 1) * PAGE_SIZE));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export async function fetchDormantList(
  tenantID: string,
  filter: DormantFilterSearch,
): Promise<DormantList> {
  const raw = await apiFetch<unknown>(`${base(tenantID)}/dormant${buildQueryString(filter)}`);
  return dormantListSchema.parse(raw);
}

export function useDormantListQuery(
  tenantID: string,
  filter: DormantFilterSearch,
  enabled = true,
) {
  return useQuery<DormantList, ApiError>({
    queryKey: ["identity-hygiene", "dormant", tenantID, filter],
    queryFn: () => fetchDormantList(tenantID, filter),
    enabled: enabled && tenantID.length > 0,
    // Hygiene moves slowly — refresh on focus + a 5-minute
    // background interval.
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
}

export interface RebuildResult {
  rows_affected: number;
  lookback_days: number;
}

export function useRebuildHygieneMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<RebuildResult, ApiError, { lookbackDays?: number } | void>({
    mutationFn: async (input) => {
      const lookback = (input && (input as { lookbackDays?: number }).lookbackDays) ?? 365;
      return withStepUp(() =>
        apiFetch<RebuildResult>(`${base(tenantID)}/rebuild?lookback_days=${lookback}`, {
          method: "POST",
        }),
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["identity-hygiene", "dormant", tenantID] });
    },
  });
}

// ── Policy ──────────────────────────────────────────────────────────────

export function usePolicyQuery(tenantID: string) {
  return useQuery<Policy, ApiError>({
    queryKey: ["identity-hygiene", "policy", tenantID],
    queryFn: async () => {
      const raw = await apiFetch<unknown>(`${base(tenantID)}/policy`);
      return policySchema.parse(raw);
    },
    enabled: tenantID.length > 0,
    staleTime: 30_000,
  });
}

function usePolicyInvalidation(tenantID: string) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ["identity-hygiene", "policy", tenantID] });
}

/**
 * Applies a change that needs no second administrator (see
 * needsCoApproval). The server refuses any other change with 409.
 */
export function usePolicyMutation(tenantID: string) {
  const invalidate = usePolicyInvalidation(tenantID);
  return useMutation<Policy, ApiError, PolicyForm>({
    mutationFn: async (input) => {
      const raw = await withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/policy`, {
          method: "PUT",
          body: input,
        }),
      );
      return policySchema.parse(raw);
    },
    onSuccess: invalidate,
  });
}

/** Proposes a change for a different administrator to approve. Nothing live changes. */
export function useProposePolicyMutation(tenantID: string) {
  const invalidate = usePolicyInvalidation(tenantID);
  return useMutation<PolicyProposal, ApiError, PolicyForm>({
    mutationFn: async (input) => {
      const raw = await withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/policy/proposals`, {
          method: "POST",
          body: input,
        }),
      );
      return policyProposalSchema.parse(raw);
    },
    onSuccess: invalidate,
  });
}

/** The second administrator's approval; the proposed policy takes effect. */
export function useApprovePolicyProposalMutation(tenantID: string) {
  const invalidate = usePolicyInvalidation(tenantID);
  return useMutation<Policy, ApiError, string>({
    mutationFn: async (proposalID) => {
      const raw = await withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/policy/proposals/${encodeURIComponent(proposalID)}/approve`, {
          method: "POST",
        }),
      );
      return policySchema.parse(raw);
    },
    onSuccess: invalidate,
  });
}

/** Declines a pending proposal; its proposer withdraws it the same way. */
export function useRejectPolicyProposalMutation(tenantID: string) {
  const invalidate = usePolicyInvalidation(tenantID);
  return useMutation<Policy, ApiError, string>({
    mutationFn: async (proposalID) => {
      const raw = await withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/policy/proposals/${encodeURIComponent(proposalID)}/reject`, {
          method: "POST",
        }),
      );
      return policySchema.parse(raw);
    },
    onSuccess: invalidate,
  });
}

// ── Revocation log ─────────────────────────────────────────────────────

export function useRevokeLogQuery(tenantID: string, limit = 100) {
  return useQuery<RevokeLogList, ApiError>({
    queryKey: ["identity-hygiene", "revocations", tenantID, limit],
    queryFn: async () => {
      const raw = await apiFetch<unknown>(
        `${base(tenantID)}/revocations?limit=${limit}`,
      );
      return revokeLogListSchema.parse(raw);
    },
    enabled: tenantID.length > 0,
    staleTime: 60_000,
  });
}

// ── Campaigns ───────────────────────────────────────────────────────────

export function useCampaignsQuery(tenantID: string) {
  return useQuery<{ data: Campaign[] }, ApiError>({
    queryKey: ["identity-hygiene", "campaigns", tenantID],
    queryFn: async () => {
      const raw = await apiFetch<unknown>(`${base(tenantID)}/campaigns`);
      return campaignListSchema.parse(raw);
    },
    enabled: tenantID.length > 0,
    staleTime: 30_000,
  });
}

export function useCampaignItemsQuery(
  tenantID: string,
  campaignID: string,
  opts?: { mine?: boolean; decision?: ItemDecision },
) {
  return useQuery<{ data: CampaignItem[] }, ApiError>({
    queryKey: ["identity-hygiene", "campaign-items", tenantID, campaignID, opts],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (opts?.mine) params.set("mine", "true");
      if (opts?.decision) params.set("decision", opts.decision);
      const qs = params.toString();
      const raw = await apiFetch<unknown>(
        `${base(tenantID)}/campaigns/${encodeURIComponent(campaignID)}/items${qs ? `?${qs}` : ""}`,
      );
      return campaignItemListSchema.parse(raw);
    },
    enabled: tenantID.length > 0 && campaignID.length > 0,
    staleTime: 30_000,
  });
}

export function useCreateCampaignMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<Campaign, ApiError, {
    name: string;
    description?: string;
    scope: { min_days_idle?: number; tone?: string };
    opens_at: string;
    closes_at: string;
    reviewer_admin_id?: string;
  }>({
    mutationFn: async (input) => {
      const raw = await withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/campaigns`, {
          method: "POST",
          body: input,
        }),
      );
      return campaignSchema.parse(raw);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["identity-hygiene", "campaigns", tenantID] });
    },
  });
}

export function useOpenCampaignMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (id) =>
      withStepUp(() =>
        apiFetch<void>(`${base(tenantID)}/campaigns/${encodeURIComponent(id)}/open`, {
          method: "POST",
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["identity-hygiene", "campaigns", tenantID] });
    },
  });
}

export function useCloseCampaignMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (id) =>
      withStepUp(() =>
        apiFetch<void>(`${base(tenantID)}/campaigns/${encodeURIComponent(id)}/close`, {
          method: "POST",
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["identity-hygiene", "campaigns", tenantID] });
      qc.invalidateQueries({ queryKey: ["identity-hygiene", "dormant", tenantID] });
    },
  });
}

export function useDecideItemMutation(tenantID: string, campaignID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { itemID: string; decision: ItemDecision; comment?: string }>({
    mutationFn: ({ itemID, decision, comment }) =>
      withStepUp(() =>
        apiFetch<void>(`${base(tenantID)}/items/${encodeURIComponent(itemID)}/decide`, {
          method: "POST",
          body: { decision, comment: comment ?? "" },
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["identity-hygiene", "campaign-items", tenantID, campaignID],
      });
      qc.invalidateQueries({ queryKey: ["identity-hygiene", "campaigns", tenantID] });
    },
  });
}
